<template>
  <Container hideFooter title="Team sign-up">
    <template v-slot:body>
      <div class="overtime-page">
        <section class="overtime-intro">
          <div class="step-badge step-badge--accent">
            <span>TEAM</span><i></i><span>OPERATIONS</span>
          </div>
          <h1>Plan your<br /><span>extra shift.</span></h1>
          <p class="flow-lede">
            Check the schedule, choose a day, and reserve your spot. We’ll keep
            the team in sync.
          </p>
          <div class="capacity-card">
            <span class="capacity-icon"
              ><IconGlyph name="calendar" :size="20"
            /></span>
            <span
              ><strong>{{ maxDailySignups }} spots per day</strong
              ><small
                >Up to {{ maxWeeklySignups }} sign-ups each week</small
              ></span
            >
          </div>
        </section>

        <section class="overtime-board">
          <div class="date-panel">
            <div class="board-heading">
              <div>
                <span class="section-overline">Choose a date</span>
                <h2>When are you available?</h2>
              </div>
              <span class="date-selected">{{ formattedSelectedDate }}</span>
            </div>
            <v-date-picker
              ref="picker"
              v-model="date"
              :allowed-dates="allowedDates"
              full-width
              class="overtime-picker"
              color="secondary"
              elevation="0"
            ></v-date-picker>
          </div>

          <div class="availability-panel">
            <div class="board-heading availability-heading">
              <div>
                <span class="section-overline">Live availability</span>
                <h2>
                  {{
                    new Date(localDate).toLocaleDateString([], {
                      month: "long",
                      day: "numeric"
                    })
                  }}
                </h2>
              </div>
              <span class="availability-count"
                >{{ filledSpots }} / {{ maxDailySignups }}</span
              >
            </div>

            <div class="availability-list">
              <div
                v-for="(item, index) in signedUpEmployees"
                :key="index"
                class="availability-row"
                :class="{ 'availability-row--open': !item }"
              >
                <span class="availability-index">{{
                  String(index + 1).padStart(2, "0")
                }}</span>
                <span v-if="item" class="availability-name"
                  >{{ item.employee
                  }}<small>{{
                    item.shift
                      ? item.shift[0].toUpperCase() +
                        item.shift.slice(1) +
                        " shift"
                      : ""
                  }}</small></span
                >
                <span v-else class="availability-open">Open spot</span>
                <IconGlyph v-if="item" name="check" :size="16" />
              </div>
            </div>

            <div class="overtime-signup">
              <div class="signup-search-label">Ready to sign up?</div>
              <UserSearch
                text="Search your name"
                returnObject
                @employee-selected="handleEmployee"
                ref="userSearch"
              />
              <button
                name="signup"
                class="primary-cta"
                type="button"
                :disabled="disbaleButton"
                @click="confirmDialog = true"
              >
                <span>{{
                  disbaleButton ? "Choose a future date" : "Sign me up"
                }}</span>
                <IconGlyph
                  v-if="!disbaleButton"
                  name="arrow-right"
                  :size="19"
                />
              </button>
            </div>
          </div>
        </section>
      </div>

      <v-dialog
        v-model="confirmDialog"
        persistent
        max-width="500px"
        content-class="kiosk-dialog-wrap"
      >
        <div class="kiosk-dialog overtime-dialog">
          <div class="dialog-heading">
            <span class="dialog-icon"
              ><IconGlyph name="calendar" :size="23"
            /></span>
            <div>
              <span class="section-overline">One last thing</span>
              <h2>Confirm your shift</h2>
            </div>
          </div>
          <p class="overtime-dialog-copy">
            You’re signing up for <strong>{{ formattedSelectedDate }}</strong
            >. Please arrive on time for your shift.
          </p>
          <label class="field-label" for="shift-type">Shift type</label>
          <div class="select-wrap">
            <select id="shift-type" v-model="shift" class="kiosk-select">
              <option disabled value="">Select a shift</option>
              <option v-for="item in items" :key="item" :value="item">{{
                item
              }}</option>
            </select>
            <IconGlyph name="chevron-down" :size="18" />
          </div>
          <div class="dialog-actions">
            <button
              type="button"
              class="secondary-cta"
              @click="confirmDialog = false"
            >
              Cancel
            </button>
            <button
              type="button"
              class="primary-cta"
              :disabled="!shift"
              @click="signUp"
            >
              Confirm sign-up
            </button>
          </div>
        </div>
      </v-dialog>
    </template>
  </Container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import Container from "@/components/Container";
import IconGlyph from "@/components/IconGlyph";
import UserSearch from "@/components/UserSearch";

export default {
  name: "Overtime",
  components: { Container, IconGlyph, UserSearch },
  data: () => ({
    date: new Date().toISOString().substr(0, 10),
    employeeName: "",
    employeeID: "",
    confirmDialog: false,
    items: ["Day Shift Only", "Both Shifts"],
    shift: ""
  }),
  watch: {
    date(value) {
      this.getOvertime({ signUpDate: value });
    }
  },
  async mounted() {
    await this.getOvertime({ signUpDate: this.date });
    await this.getOverTimeConfig();
  },
  computed: {
    ...mapGetters({
      overtimeStore: "getOvertime",
      maxDailySignups: "getMaxDailySignups",
      maxWeeklySignups: "getMaxWeeklySignups",
      blackOutDates: "getBlackOutDates"
    }),
    localDate() {
      let date = new Date(new Date(this.date).setHours(0));
      date = new Date(date.setDate(new Date(date).getDate() + 1));
      return date;
    },
    formattedSelectedDate() {
      return new Date(this.localDate).toLocaleDateString([], {
        weekday: "long",
        month: "long",
        day: "numeric"
      });
    },
    filledSpots() {
      return this.overtimeStore.length;
    },
    disbaleButton() {
      return new Date(this.date) < new Date(this.formatDate(new Date()));
    },
    signedUpEmployees() {
      const employees = this.overtimeStore.map(item => ({
        employee: item.employeeName,
        shift: item.shift
      }));
      while (employees.length < this.maxDailySignups) employees.push("");
      return employees;
    }
  },
  methods: {
    ...mapActions({
      createOvertime: "createOvertime",
      getOvertime: "getOvertime",
      getOverTimeConfig: "getOverTimeConfig"
    }),
    clearData() {
      this.$refs.userSearch.clear();
      this.employeeName = "";
      this.employeeID = "";
      this.shift = "";
    },
    handleEmployee(employee) {
      if (employee) {
        this.employeeName = employee.displayName;
        this.employeeID = employee.employeeID;
      }
    },
    async signUp() {
      if (!this.employeeName) {
        this.$store.dispatch("setSnackbar", {
          text: "Please search for your name.",
          color: "error"
        });
        return;
      }
      const signedUpAlready = await this.getOvertime({
        signUpDate: this.date,
        employeeName: this.employeeName,
        setData: false
      });
      const weekSignUpCount = await this.getOvertime({
        weekNumber: this.getWeekNumber(this.localDate),
        employeeName: this.employeeName,
        setData: false
      });
      const daySignUpCount = await this.getOvertime({
        signUpDate: this.date,
        setData: false
      });

      if (signedUpAlready.length > 0) {
        this.$store.dispatch("setSnackbar", {
          text: "You have already signed up for this day.",
          color: "error"
        });
      } else if (weekSignUpCount.length > this.maxWeeklySignups - 1) {
        this.$store.dispatch("setSnackbar", {
          text: `You can only sign up ${this.maxWeeklySignups} time(s) a week.`,
          color: "error"
        });
      } else if (daySignUpCount.length >= this.maxDailySignups) {
        this.$store.dispatch("setSnackbar", {
          text: "This day is fully scheduled.",
          color: "error"
        });
      } else {
        await this.createOvertime({
          signUpDate: this.localDate.toISOString().substr(0, 10),
          weekNumber: this.getWeekNumber(this.localDate),
          employeeName: this.employeeName,
          employeeID: this.employeeID,
          shift: this.shift === "Day Shift Only" ? "day" : "both"
        });
        this.clearData();
        await this.getOvertime({ signUpDate: this.date });
        this.confirmDialog = false;
      }
    },
    allowedDates(value) {
      if (this.blackOutDates.includes(value)) return false;
      return ![5].includes(new Date(value).getDay());
    },
    formatDate(value) {
      return new Date(value).toISOString().substr(0, 10);
    },
    getWeekNumber(value) {
      const date = new Date(value);
      const utcDate = new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
      );
      utcDate.setUTCDate(utcDate.getUTCDate() + 4 - (utcDate.getUTCDay() || 7));
      const yearStart = new Date(Date.UTC(utcDate.getUTCFullYear(), 0, 1));
      return Math.ceil(((utcDate - yearStart) / 86400000 + 1) / 7);
    }
  }
};
</script>

<style>
.overtime-page {
  display: grid;
  grid-template-columns: minmax(250px, 0.65fr) minmax(700px, 1.35fr);
  align-items: center;
  gap: clamp(42px, 7vw, 120px);
  min-height: calc(100vh - 189px);
}

.overtime-intro {
  max-width: 360px;
  padding-left: clamp(10px, 2.5vw, 40px);
}

.overtime-intro h1 {
  margin: 23px 0 15px;
  color: var(--ink);
  font-family: Eurosoft, -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  font-size: clamp(49px, 4.5vw, 70px);
  font-weight: 500;
  letter-spacing: -0.055em;
  line-height: 0.95;
}

.overtime-intro h1 span {
  color: var(--orange);
}

.capacity-card {
  display: flex;
  align-items: center;
  gap: 11px;
  margin-top: 33px;
  padding: 14px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.72);
}

.capacity-icon {
  display: grid;
  width: 37px;
  height: 37px;
  border-radius: 11px;
  background: var(--accent-soft);
  color: var(--accent);
  place-items: center;
}

.capacity-card > span:last-child {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.capacity-card strong {
  color: var(--ink);
  font-size: 12px;
}

.capacity-card small {
  color: var(--muted);
  font-size: 10px;
}

.overtime-board {
  display: grid;
  grid-template-columns: 0.88fr 1.12fr;
  overflow: hidden;
  border: 1px solid rgba(30, 41, 42, 0.08);
  border-radius: 25px;
  background: rgba(255, 255, 255, 0.88);
  box-shadow: 0 24px 60px rgba(30, 41, 42, 0.08);
}

.date-panel,
.availability-panel {
  padding: 29px;
}

.date-panel {
  border-right: 1px solid #e8ebe6;
}

.board-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 15px;
}

.board-heading h2 {
  margin: 7px 0 0;
  color: var(--ink);
  font-size: 20px;
  font-weight: 850;
  letter-spacing: -0.04em;
}

.date-selected {
  margin-top: 4px;
  color: var(--accent);
  font-size: 10px;
  font-weight: 850;
  text-align: right;
}

.overtime-picker {
  width: 100% !important;
  margin-top: 16px;
  background: transparent !important;
}

.overtime-picker .v-date-picker-table,
.overtime-picker .v-date-picker-header,
.overtime-picker .v-picker__body {
  background: transparent !important;
}

.overtime-picker .v-date-picker-header__value button,
.overtime-picker .v-date-picker-table .v-btn {
  color: var(--ink) !important;
}

.overtime-picker .v-date-picker-table .v-btn.v-btn--active {
  color: white !important;
}

.availability-heading {
  padding-bottom: 19px;
  border-bottom: 1px solid #eceeea;
}

.availability-count {
  padding: 8px 11px;
  border-radius: 9px;
  background: var(--accent-soft);
  color: var(--accent);
  font-size: 11px;
  font-weight: 900;
}

.availability-list {
  max-height: 255px;
  margin: 12px 0 20px;
  overflow-y: auto;
}

.availability-row {
  display: flex;
  align-items: center;
  min-height: 38px;
  border-bottom: 1px solid #f0f1ed;
}

.availability-index {
  width: 35px;
  color: #a6afa9;
  font-size: 10px;
  font-weight: 850;
}

.availability-name,
.availability-open {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 3px;
  color: var(--ink);
  font-size: 11px;
  font-weight: 800;
}

.availability-name small {
  color: var(--muted);
  font-size: 9px;
  font-weight: 600;
}

.availability-row > .icon-glyph {
  color: var(--accent);
}

.availability-row--open .availability-open {
  color: #b4beb8;
  font-weight: 650;
}

.overtime-signup {
  padding-top: 18px;
  border-top: 1px solid #eceeea;
}

.signup-search-label {
  margin-bottom: 8px;
  color: var(--ink);
  font-size: 11px;
  font-weight: 850;
}

.overtime-signup .primary-cta {
  width: 100%;
  margin-top: 11px;
}

.overtime-signup .user-search-wrap {
  min-height: 46px;
}

.overtime-signup
  .kiosk-autocomplete.v-text-field
  > .v-input__control
  > .v-input__slot {
  min-height: 44px !important;
}

.overtime-dialog-copy {
  margin: 23px 0 22px;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.6;
}

.overtime-dialog-copy strong {
  color: var(--ink);
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 26px;
}

.secondary-cta {
  min-height: 48px;
  padding: 0 17px;
  border: 1px solid #dfe3dd;
  border-radius: 12px;
  background: white;
  color: var(--ink);
  font: inherit;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
}

.secondary-cta:hover {
  border-color: var(--accent);
  color: var(--accent);
}

@media (max-width: 1100px) {
  .overtime-page {
    grid-template-columns: 1fr;
    gap: 24px;
    min-height: 0;
  }

  .overtime-intro {
    max-width: 700px;
    padding-left: 0;
  }

  .overtime-intro h1 {
    margin-top: 13px;
    font-size: 56px;
  }

  .capacity-card {
    display: inline-flex;
    margin-top: 20px;
  }
}

@media (max-width: 740px) {
  .overtime-board {
    grid-template-columns: 1fr;
  }

  .date-panel {
    border-right: 0;
    border-bottom: 1px solid #e8ebe6;
  }
}

@media (orientation: landscape) and (min-width: 768px) and (max-height: 820px) {
  .overtime-page {
    min-height: calc(100vh - 134px);
  }

  .date-panel,
  .availability-panel {
    padding: 22px;
  }

  .overtime-picker {
    margin-top: 8px;
  }

  .availability-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 20px;
    max-height: none;
    margin-top: 8px;
    margin-bottom: 12px;
  }

  .overtime-signup {
    padding-top: 12px;
  }

  .overtime-signup .primary-cta {
    min-height: 46px;
  }
}
</style>
